import { Currency } from '@fp3/shared'

const CURRENCY_SIGNS: Partial<Record<Currency, string>> = {
  [Currency.USD]: '$',
  [Currency.EUR]: '€',
  [Currency.GBP]: '£',
  [Currency.JPY]: '¥',
  [Currency.CNY]: '¥',
  [Currency.RUB]: '₽',
  [Currency.UAH]: '₴',
  [Currency.BTC]: '₿',
  [Currency.RSD]: 'din.',
  [Currency.CHF]: 'Fr',
  [Currency.SEK]: 'kr',
  [Currency.NOK]: 'kr',
  [Currency.DKK]: 'kr',
  [Currency.PLN]: 'zł',
  [Currency.INR]: '₹',
  [Currency.KRW]: '₩',
  [Currency.TRY]: '₺',
  [Currency.BRL]: 'R$',
  [Currency.ZAR]: 'R',
  [Currency.THB]: '฿',
  [Currency.ILS]: '₪',
  [Currency.PHP]: '₱',
  [Currency.VND]: '₫',
  [Currency.GEL]: '₾',
  [Currency.KZT]: '₸',
  [Currency.AUD]: 'A$',
  [Currency.CAD]: 'C$',
  [Currency.NZD]: 'NZ$',
  [Currency.SGD]: 'S$',
  [Currency.HKD]: 'HK$',
  [Currency.MXN]: 'MX$',
  [Currency.CZK]: 'Kč',
  [Currency.HUF]: 'Ft',
  [Currency.RON]: 'lei',
  [Currency.BGN]: 'лв',
}

const numberFormatter = new Intl.NumberFormat('en-US', {
  style: 'decimal',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export const formatAmount = (amount: number, currency: Currency | string): string => {
  const symbol = CURRENCY_SIGNS[currency as Currency]
  // symbol-less currencies keep the sign with the digits (e.g. "-500.00 RSD")
  if (!symbol) return `${numberFormatter.format(amount)} ${currency}`
  // symboled currencies put the minus BEFORE the symbol: -₽500.00, not ₽-500.00 (#305)
  return `${amount < 0 ? '-' : ''}${symbol}${numberFormatter.format(Math.abs(amount))}`
}

export const formatNumber = (amount: number): string => numberFormatter.format(amount)

// per-share price formatting: keeps at least 2 fraction digits but shows up to 4 so fine-tick,
// low-priced instruments (e.g. IRAO 2.5895, HYDR 0.3054) are not truncated to 2 decimals. use for
// per-share prices (last/avg-entry/arm/anchor) — NOT for money totals or percentages, which stay at 2.
export const formatPrice = (price: number): string =>
  price.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 4 })

/** formats a number in compact form (e.g. 1200 → "1.2k", 45000 → "45k", 2.5e12 → "2.5T") */
export const formatCompactAmount = (value: number): string => {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000_000_000) return `${sign}${+(abs / 1_000_000_000_000).toFixed(1)}T`
  if (abs >= 1_000_000_000) return `${sign}${+(abs / 1_000_000_000).toFixed(1)}B`
  if (abs >= 1_000_000) return `${sign}${+(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}${+(abs / 1_000).toFixed(1)}k`
  return `${sign}${Math.round(abs)}`
}

/** compact amount prefixed with the currency sign (e.g. 2.5e12, RUB → "₽2.5T") */
export const formatCompactMoney = (value: number, currency: Currency | string): string => {
  const compact = formatCompactAmount(value)
  const sign = CURRENCY_SIGNS[currency as Currency]
  return sign ? `${sign}${compact}` : `${compact} ${currency}`
}

const signPrefix = (value: number): string => (value > 0 ? '+' : value < 0 ? '-' : '')

/** signed money with the currency sign, e.g. 1234, RUB → "+₽1,234.00"; -5, RUB → "-₽5.00" */
export const formatSignedAmount = (value: number, currency: Currency | string): string =>
  `${signPrefix(value)}${formatAmount(Math.abs(value), currency)}`

/** signed compact money, e.g. 12300, RUB → "+₽12.3k"; -4500, RUB → "-₽4.5k" */
export const formatSignedCompactMoney = (value: number, currency: Currency | string): string =>
  `${signPrefix(value)}${formatCompactMoney(Math.abs(value), currency)}`

/** signed percent to 2 decimals, e.g. 3.4 → "+3.40%"; -1.2 → "-1.20%"; 0 → "0.00%" */
export const formatSignedPercent = (value: number): string =>
  `${signPrefix(value)}${Math.abs(value).toFixed(2)}%`

/** returns true for finite numbers including 0, false for null/undefined/NaN/Infinity */
export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

/** formats "YYYY-MM" to "Jan 24" style label for chart axes */
export const formatMonthLabel = (month: string): string => {
  const [year, m] = month.split('-')
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const monthIdx = parseInt(m!, 10) - 1
  return `${monthNames[monthIdx]} ${year!.slice(2)}`
}

/** formats a date as a 24h "HH:MM" time, e.g. for order created/completed times */
export const formatTime = (date: Date | string): string => {
  const d = new Date(date)
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

/** true when the given date falls on the current local calendar day */
export const isToday = (date: Date | string): boolean => {
  const d = new Date(date)
  const now = new Date()
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  )
}

export const formatShortDate = (date: Date | string): string => {
  const d = new Date(date)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** formats a date as a numeric "DD.MM.YYYY", e.g. for compact date-range columns */
export const formatNumericDate = (date: Date | string): string => {
  const d = new Date(date)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${d.getFullYear()}`
}

/**
 * formats a hold/position duration showing up to two leading units (mo / d / h / m),
 * e.g. "2mo 5d", "3d 4h", "12m". Used wherever a span can run from minutes to months.
 */
export const formatHoldDuration = (ms: number): string => {
  if (ms <= 0) return '0m'
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`
  const totalMin = Math.floor(ms / 60_000)
  const months = Math.floor(totalMin / (60 * 24 * 30))
  const days = Math.floor((totalMin % (60 * 24 * 30)) / (60 * 24))
  const hours = Math.floor((totalMin % (60 * 24)) / 60)
  const mins = totalMin % 60
  const parts: string[] = []
  if (months) parts.push(`${months}mo`)
  if (days) parts.push(`${days}d`)
  if (hours) parts.push(`${hours}h`)
  if (mins) parts.push(`${mins}m`)
  return parts.slice(0, 2).join(' ') || '0m'
}

export const formatDateTime = (date: Date | string): string => {
  const d = new Date(date)
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

/** full date-time including milliseconds, e.g. "07 Jun 2026, 14:32:05.123" */
export const formatDateTimeMs = (date: Date | string): string => {
  const d = new Date(date)
  return `${formatDateTime(d)}.${String(d.getMilliseconds()).padStart(3, '0')}`
}

/** formats a millisecond duration as a compact label, e.g. "1h 5m", "2m 3s", "8s", "850ms" */
export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

/** human calendar span between two dates, e.g. "2y 3mo", "5mo 13d", "15d" */
export const formatDateSpan = (from: Date | string, to: Date | string): string => {
  const a = new Date(from)
  const b = new Date(to)
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return ''
  let years = b.getFullYear() - a.getFullYear()
  let months = b.getMonth() - a.getMonth()
  let days = b.getDate() - a.getDate()
  if (days < 0) {
    months -= 1
    days += new Date(b.getFullYear(), b.getMonth(), 0).getDate()
  }
  if (months < 0) {
    years -= 1
    months += 12
  }
  const parts: string[] = []
  if (years) parts.push(`${years}y`)
  if (months) parts.push(`${months}mo`)
  if (days) parts.push(`${days}d`)
  return parts.length ? parts.join(' ') : '0d'
}

export const formatMoney = (
  value: number,
  signDisplay: Intl.NumberFormatOptions['signDisplay'] = 'auto'
): string => {
  if (value === 0) return '—'
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay,
  }).format(value)
}

/** return white or black depending on which has better contrast against the given hex color */
export const getContrastTextColor = (hex: string): string => {
  const c = hex.replace('#', '')
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  // perceived luminance (ITU-R BT.709)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? '#000000' : '#ffffff'
}
